/**
 * Visual Template Editor Page
 *
 * Main page component that orchestrates the inline visual editor.
 * Loads a template JSON, renders it with EditableRenderer,
 * manages state changes, and saves back to the server.
 */

import React, { useState, useEffect, useCallback, useRef, useContext, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'js-cookie';
import Swal from 'sweetalert2';
import AuthContext from '../context/AuthContext';
import API_BASE_URL from '../config/api';
import { ArrowLeft, Sparkles } from 'lucide-react';
import EditableRenderer from '../components/VisualEditor/EditableRenderer';
import EditorTopBar from '../components/VisualEditor/EditorTopBar';
import ThemePanel from '../components/VisualEditor/ThemePanel';
import BusinessInfoPanel from '../components/VisualEditor/BusinessInfoPanel';
import SectionLibrary from '../components/VisualEditor/SectionLibrary';
import VersionHistory from '../components/VisualEditor/VersionHistory';
import FloatingToolbar from '../components/VisualEditor/FloatingToolbar';
import BreadcrumbBar from '../components/VisualEditor/BreadcrumbBar';
import PropertiesPanel from '../components/VisualEditor/PropertiesPanel';
import AnimationPanel from '../components/VisualEditor/AnimationPanel';
import NavbarConfigPanel from '../components/VisualEditor/previews/NavbarConfigPanel';
import FooterConfigPanel from '../components/VisualEditor/previews/FooterConfigPanel';
import { UndoRedoManager } from '../components/VisualEditor/UndoRedoManager';
import { updateContentByBinding, deepClone, updateNodeStyle, findNodeByPath } from '../components/VisualEditor/bindingUtils';

// Recursively find a node by component type and update its attributes
function updateBlockAttributes(node, componentType, newAttributes) {
  if (!node || typeof node !== 'object') return false;
  if (node.type === componentType) {
    node.attributes = { ...node.attributes, ...newAttributes };
    return true;
  }
  if (Array.isArray(node.children)) {
    for (const child of node.children) {
      if (updateBlockAttributes(child, componentType, newAttributes)) return true;
    }
  }
  return false;
}

// Recursively find a node by component type and return its attributes
function findComponentAttributes(templateJSON, componentType) {
  if (!templateJSON?.blocks) return {};
  for (const block of templateJSON.blocks) {
    const attrs = findNodeAttributes(block.structure, componentType);
    if (attrs) return attrs;
  }
  return {};
}

function findNodeAttributes(node, componentType) {
  if (!node || typeof node !== 'object') return null;
  if (node.type === componentType) return node.attributes || {};
  if (Array.isArray(node.children)) {
    for (const child of node.children) {
      const found = findNodeAttributes(child, componentType);
      if (found) return found;
    }
  }
  return null;
}

const VisualTemplateEditor = () => {
  const { authToken, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const accessToken = authToken?.access;

  const templateNo = searchParams.get('template') ? parseInt(searchParams.get('template'), 10) : null;
  const pageType = searchParams.get('page') || 'main';

  // State
  const [templateJSON, setTemplateJSON] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [editMode, setEditMode] = useState(true);
  const [previewDevice, setPreviewDevice] = useState('desktop');
  const [showThemePanel, setShowThemePanel] = useState(false);
  const [showBusinessPanel, setShowBusinessPanel] = useState(false);
  const [showSectionLibrary, setShowSectionLibrary] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [undoInfo, setUndoInfo] = useState({ canUndo: false, canRedo: false });
  const [undoToast, setUndoToast] = useState(null);
  const [pendingFiles, setPendingFiles] = useState({});
  const [templateName, setTemplateName] = useState('');
  const [selectedElement, setSelectedElement] = useState(null);
  const [editingNodePath, setEditingNodePath] = useState(null);
  const [showPropertiesPanel, setShowPropertiesPanel] = useState(false);
  const [hintsDismissed, setHintsDismissed] = useState(!!localStorage.getItem('editor_hints_dismissed'));
  const [autoSaveStatus, setAutoSaveStatus] = useState(null); // null | 'saving' | 'saved'
  const [isDraftSaving, setIsDraftSaving] = useState(false);
  const [activeConfigPanel, setActiveConfigPanel] = useState(null); // null | 'navbar' | 'footer'
  const [zoomLevel, setZoomLevel] = useState(100); // Canvas zoom (25–200%)
  const [showAnimationPanel, setShowAnimationPanel] = useState(false);
  const [copiedStyle, setCopiedStyle] = useState(null); // For copy/paste style feature
  const [navbarAttrs, setNavbarAttrs] = useState(null); // Persistent navbar attributes for wrapping pages
  const [footerAttrs, setFooterAttrs] = useState(null); // Persistent footer attributes for wrapping pages

  const undoManager = useRef(null);
  const canvasRef = useRef(null);
  const autoSaveTimerRef = useRef(null);
  // Master theme persists across page switches — ensures consistent theme on all pages
  const masterThemeRef = useRef(null);
  // Track if navbar/footer attrs were modified from a non-layout page
  const navbarDirtyRef = useRef(false);
  const footerDirtyRef = useRef(false);
  // Ref to access handleSave from handlePageChange without hook ordering issues
  const handleSaveRef = useRef(null);

  // Helper: normalize v1.0 single-structure templates (navbar/footer) into v2.0 blocks format
  const normalizeTemplateJSON = useCallback((data, type) => {
    if (data && data.blocks && data.blocks.length > 0) return data; // already v2.0
    if (data && data.structure) {
      // v1.0 format: single structure at top level → wrap into blocks
      return {
        version: '2.0',
        page_type: type || data.component_type || 'navbar',
        template_id: data.template_id || templateNo,
        template_name: data.template_name || `Template ${templateNo}`,
        theme: data.theme || {},
        content: data.content || {},
        blocks: [{
          id: data.component_type || type || 'main-block',
          type: 'section',
          position: 1,
          enabled: true,
          visible_on: ['desktop', 'tablet', 'mobile'],
          structure: data.structure,
        }],
      };
    }
    return data;
  }, [templateNo]);

  // Helper: create empty page JSON for any page type
  const createEmptyPageJSON = useCallback((type) => ({
    version: '2.0',
    page_type: type,
    template_id: templateNo,
    template_name: `Template ${templateNo}`,
    blocks: [],
    content: {},
    theme: {}
  }), [templateNo]);

  // Helper: try recovering a local draft
  const tryRecoverDraft = useCallback((currentJSON) => {
    try {
      const draftKey = `editor_draft_${templateNo}_${pageType}`;
      const draftRaw = localStorage.getItem(draftKey);
      if (draftRaw) {
        const draft = JSON.parse(draftRaw);
        if (draft.timestamp && (Date.now() - draft.timestamp) < 86400000) {
          const draftStr = JSON.stringify(draft.json?.content);
          const currentStr = JSON.stringify(currentJSON?.content);
          if (draftStr !== currentStr) {
            Swal.fire({
              title: 'Recover Unsaved Changes?',
              text: 'Found a local draft from ' + new Date(draft.timestamp).toLocaleString() + '. Would you like to restore it?',
              icon: 'question',
              showCancelButton: true,
              confirmButtonText: 'Restore Draft',
              cancelButtonText: 'Discard Draft',
              confirmButtonColor: '#3b82f6',
            }).then((result) => {
              if (result.isConfirmed && draft.json) {
                setTemplateJSON(draft.json);
                setIsDirty(true);
                undoManager.current?.push(draft.json, 'Restore local draft');
              }
              localStorage.removeItem(draftKey);
            });
          }
        }
      }
    } catch (e) {
      // Ignore draft recovery errors
    }
  }, [templateNo, pageType]);

  // Initialize template state from loaded data
  // Theme propagation: main page's theme is the single source of truth.
  // Non-main pages always inherit the master theme (overrides base template themes).
  const initializeTemplate = useCallback((data, name, isMainPage) => {
    const hasTheme = data?.theme?.colors && Object.keys(data.theme.colors).length > 0;

    if (isMainPage && hasTheme) {
      // Main page owns the theme — cache it as master
      masterThemeRef.current = JSON.parse(JSON.stringify(data.theme));
    } else if (!isMainPage && masterThemeRef.current) {
      // Non-main page — always use master theme for consistency
      data = { ...data, theme: JSON.parse(JSON.stringify(masterThemeRef.current)) };
    } else if (!isMainPage && hasTheme && !masterThemeRef.current) {
      // Non-main page loaded directly with no master yet — use its own theme as fallback
      masterThemeRef.current = JSON.parse(JSON.stringify(data.theme));
    }

    setTemplateJSON(data);
    setTemplateName(name);
    undoManager.current = new UndoRedoManager(data);
    undoManager.current.subscribe(setUndoInfo);
    setIsDirty(false);
    tryRecoverDraft(data);
  }, [tryRecoverDraft]);

  // Load template JSON from server
  useEffect(() => {
    if (!accessToken || !templateNo) {
      setIsLoading(false);
      return;
    }

    const loadTemplate = async () => {
      setIsLoading(true);

      // For non-main pages, ensure we have the master theme from the main page
      if (pageType !== 'main' && !masterThemeRef.current) {
        try {
          const mainResp = await axios.get(
            `${API_BASE_URL}/api/template/${templateNo}/page/main`,
            { headers: { Authorization: `Bearer ${accessToken}` } }
          );
          const mainData = mainResp.data?.data || mainResp.data;
          if (mainData?.theme?.colors && Object.keys(mainData.theme.colors).length > 0) {
            masterThemeRef.current = JSON.parse(JSON.stringify(mainData.theme));
          }
        } catch {
          // Ignore — initializeTemplate will handle missing theme
        }
      }

      // Helper: load base template from static files
      const fetchBaseTemplate = async (type) => {
        const pathMap = {
          'main': `template_${templateNo}.json`,
          'about': `about/about${templateNo}.json`,
          'shop': `shop/shop${templateNo}.json`,
          'product': `product/product${templateNo}.json`,
          'contact': `contact/contact${templateNo}.json`,
          'navbar': `navbar/navbar${templateNo}.json`,
          'footer': `footer/footer${templateNo}.json`,
          'privacy': `privacy/privacy${templateNo}.json`,
          'shipping': `shipping/shipping${templateNo}.json`,
          'terms': `terms/terms${templateNo}.json`,
          'faqs': `faqs/faqs${templateNo}.json`,
        };
        const path = pathMap[type || 'main'] || pathMap['main'];
        try {
          return (await axios.get(`/static/template_bases/${path}`)).data;
        } catch {
          return (await axios.get(`${API_BASE_URL}/static/template_bases/${path}`)).data;
        }
      };

      // Load navbar/footer for persistent display (skip when editing them standalone)
      const isLayoutPage = pageType === 'navbar' || pageType === 'footer';
      if (!isLayoutPage) {
        const extractAttrs = (data) => {
          // v1.0: data.structure.attributes, v2.0: data.blocks[0].structure.attributes
          if (data?.structure?.attributes) return data.structure.attributes;
          if (data?.blocks?.[0]?.structure?.attributes) return data.blocks[0].structure.attributes;
          return null;
        };
        // Load navbar
        try {
          const navResp = await axios.get(
            `${API_BASE_URL}/api/template/${templateNo}/page/navbar`,
            { headers: { Authorization: `Bearer ${accessToken}` } }
          );
          const navData = navResp.data?.data || navResp.data;
          let attrs = extractAttrs(navData);
          if (!attrs) {
            const baseNav = await fetchBaseTemplate('navbar');
            attrs = extractAttrs(baseNav);
          }
          if (attrs) setNavbarAttrs(attrs);
        } catch {
          try {
            const baseNav = await fetchBaseTemplate('navbar');
            const attrs = extractAttrs(baseNav);
            if (attrs) setNavbarAttrs(attrs);
          } catch { /* no navbar available */ }
        }
        // Load footer
        try {
          const footResp = await axios.get(
            `${API_BASE_URL}/api/template/${templateNo}/page/footer`,
            { headers: { Authorization: `Bearer ${accessToken}` } }
          );
          const footData = footResp.data?.data || footResp.data;
          let attrs = extractAttrs(footData);
          if (!attrs) {
            const baseFoot = await fetchBaseTemplate('footer');
            attrs = extractAttrs(baseFoot);
          }
          if (attrs) setFooterAttrs(attrs);
        } catch {
          try {
            const baseFoot = await fetchBaseTemplate('footer');
            const attrs = extractAttrs(baseFoot);
            if (attrs) setFooterAttrs(attrs);
          } catch { /* no footer available */ }
        }
      } else {
        setNavbarAttrs(null);
        setFooterAttrs(null);
      }

      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/template/${templateNo}/page/${pageType}`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );

        const responseData = response.data;
        const templateData = responseData.data || responseData;

        // Check if blocks are actually renderable (have structure HTML)
        const hasValidBlocks = templateData?.blocks?.length > 0 &&
          templateData.blocks.some(b => b.structure);

        const isMain = pageType === 'main';

        if (templateData && templateData.blocks && hasValidBlocks) {
          initializeTemplate(
            templateData,
            templateData.template_name || responseData.temp_name || `Template ${templateNo}`,
            isMain
          );
        } else if (!isMain) {
          // Non-main page with no valid saved blocks — try base template, fallback to empty
          try {
            const rawJSON = await fetchBaseTemplate(pageType);
            const baseJSON = normalizeTemplateJSON(rawJSON, pageType);
            if (baseJSON && baseJSON.blocks && baseJSON.blocks.length > 0) {
              initializeTemplate(baseJSON, baseJSON.template_name || `Template ${templateNo} — ${pageType}`, false);
            } else {
              const emptyPage = createEmptyPageJSON(pageType);
              initializeTemplate(emptyPage, `Template ${templateNo} — ${pageType}`, false);
              setShowSectionLibrary(true);
            }
          } catch {
            const emptyPage = createEmptyPageJSON(pageType);
            initializeTemplate(emptyPage, `Template ${templateNo} — ${pageType}`, false);
            setShowSectionLibrary(true);
          }
        } else {
          // Main page — saved data is incomplete, merge with base template
          console.warn('Saved template has incomplete blocks, merging with base template...');
          const baseJSON = await fetchBaseTemplate('main');
          if (baseJSON && baseJSON.blocks) {
            const merged = JSON.parse(JSON.stringify(baseJSON));
            if (templateData?.theme) {
              merged.theme = {
                ...(merged.theme || {}),
                colors: { ...(merged.theme?.colors || {}), ...(templateData.theme.colors || {}) },
                typography: { ...(merged.theme?.typography || {}), ...(templateData.theme.typography || {}) },
              };
            }
            if (templateData?.content) {
              merged.content = { ...(merged.content || {}), ...templateData.content };
            }
            if (templateData?.brand_identity) {
              merged.brand_identity = { ...(merged.brand_identity || {}), ...templateData.brand_identity };
            }
            initializeTemplate(
              merged,
              merged.template_name || responseData.temp_name || `Template ${templateNo}`,
              true
            );
          } else {
            throw new Error('Invalid base template');
          }
        }
      } catch (err) {
        console.error('Failed to load saved template:', err);
        // Try loading base template for this page type
        try {
          const rawJSON = await fetchBaseTemplate(pageType);
          const baseJSON = normalizeTemplateJSON(rawJSON, pageType);
          if (baseJSON && baseJSON.blocks && baseJSON.blocks.length > 0) {
            initializeTemplate(baseJSON, baseJSON.template_name || `Template ${templateNo}`, pageType === 'main');
          } else {
            const emptyPage = createEmptyPageJSON(pageType);
            initializeTemplate(emptyPage, `Template ${templateNo} — ${pageType}`, pageType === 'main');
            if (pageType !== 'main') setShowSectionLibrary(true);
          }
        } catch (baseErr) {
          console.error('Failed to load base template:', baseErr);
          if (pageType === 'main') {
            Swal.fire('Error', 'Could not load template. Please try again.', 'error');
            navigate('/template-builder/' + templateNo);
          } else {
            const emptyPage = createEmptyPageJSON(pageType);
            initializeTemplate(emptyPage, `Template ${templateNo} — ${pageType}`, false);
            setShowSectionLibrary(true);
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadTemplate();
  }, [accessToken, templateNo, pageType, navigate, createEmptyPageJSON, initializeTemplate]);

  // Warn on tab close with unsaved changes
  useEffect(() => {
    const handler = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Save draft to localStorage (debounced 2s)
  useEffect(() => {
    if (!templateJSON || !templateNo) return;
    const timer = setTimeout(() => {
      try {
        const key = `editor_draft_${templateNo}_${pageType}`;
        localStorage.setItem(key, JSON.stringify({
          json: templateJSON,
          timestamp: Date.now(),
          templateNo,
          pageType
        }));
      } catch (e) {
        // localStorage full or unavailable - silently ignore
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, [templateJSON, templateNo, pageType]);

  // Handle text content changes
  const handleContentChange = useCallback((bindingPath, newValue, nodePath) => {
    setTemplateJSON(prev => {
      if (!prev) return prev;
      const newJSON = deepClone(prev);

      if (bindingPath) {
        newJSON.content = updateContentByBinding(bindingPath, newValue, newJSON.content);
      } else if (nodePath) {
        const parts = nodePath.split('.');
        let node = newJSON;
        for (const part of parts) {
          if (Array.isArray(node)) {
            node = node[parseInt(part, 10)];
          } else {
            node = node[part];
          }
        }
        if (node) node.content = newValue;
      }

      undoManager.current?.push(newJSON, `Edit text: ${bindingPath || nodePath}`);
      return newJSON;
    });
    setIsDirty(true);
  }, []);

  // Handle image replacement
  const handleImageReplace = useCallback((bindingPath, file, nodePath) => {
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);
    setTemplateJSON(prev => {
      if (!prev) return prev;
      const newJSON = deepClone(prev);

      if (bindingPath) {
        newJSON.content = updateContentByBinding(bindingPath, previewUrl, newJSON.content);
        setPendingFiles(pf => ({ ...pf, [bindingPath]: file }));
      } else if (nodePath) {
        const parts = nodePath.split('.');
        let node = newJSON;
        for (const part of parts) {
          if (Array.isArray(node)) node = node[parseInt(part, 10)];
          else node = node[part];
        }
        if (node?.attributes) node.attributes.src = previewUrl;
        setPendingFiles(pf => ({ ...pf, [nodePath]: file }));
      }

      undoManager.current?.push(newJSON, `Replace image: ${bindingPath || nodePath}`);
      return newJSON;
    });
    setIsDirty(true);
  }, []);

  // Handle theme changes — updates current page and master theme for cross-page consistency
  const handleThemeChange = useCallback((newTheme) => {
    setTemplateJSON(prev => {
      if (!prev) return prev;
      const newJSON = { ...deepClone(prev), theme: newTheme };
      undoManager.current?.push(newJSON, 'Update theme');
      return newJSON;
    });
    setIsDirty(true);
    // Persist as master theme so other pages pick it up
    masterThemeRef.current = JSON.parse(JSON.stringify(newTheme));
  }, []);

  // Handle business info changes
  const handleBusinessInfoChange = useCallback((newContent) => {
    setTemplateJSON(prev => {
      if (!prev) return prev;
      const newJSON = deepClone(prev);
      newJSON.content = { ...newJSON.content, ...newContent };
      undoManager.current?.push(newJSON, 'Update business info');
      return newJSON;
    });
    setIsDirty(true);
  }, []);

  // Handle opening config panel for navbar/footer
  const handleOpenConfigPanel = useCallback((panelType) => {
    setActiveConfigPanel(panelType);
    setShowThemePanel(false);
    setShowBusinessPanel(false);
    setShowSectionLibrary(false);
    setShowVersionHistory(false);
    setShowAnimationPanel(false);
    setShowPropertiesPanel(false);
    setSelectedElement(null);
    setEditingNodePath(null);
  }, []);

  // Handle navbar/footer attribute changes — updates the block's structure.attributes in the JSON
  // Uses functional state update to avoid stale closure issues during fast typing
  const handleComponentAttributeChange = useCallback((componentType, newAttributes) => {
    setTemplateJSON(prev => {
      if (!prev) return prev;
      const newJSON = deepClone(prev);
      for (const block of newJSON.blocks) {
        if (updateBlockAttributes(block.structure, componentType, newAttributes)) break;
      }
      undoManager.current?.push(newJSON, `Update ${componentType} settings`);
      return newJSON;
    });
    setIsDirty(true);
  }, []);

  // Handle element click (single-click = select, text = also enter edit mode)
  const handleElementClick = useCallback((info) => {
    // info: { type, domNode, nodePath, structure, tagName, bindingPath }
    setSelectedElement(info);
    setShowPropertiesPanel(true);
    // Close other panels
    setShowThemePanel(false);
    setShowBusinessPanel(false);
    setShowSectionLibrary(false);
    setShowVersionHistory(false);
    // For text elements, single-click enters edit mode immediately (matching old UX)
    if (info.type === 'text' && info.nodePath) {
      setEditingNodePath(info.nodePath);
    } else {
      setEditingNodePath(null);
    }
  }, []);

  // Handle element double-click (enter inline text editing)
  const handleElementDoubleClick = useCallback((nodePath) => {
    setEditingNodePath(nodePath);
  }, []);

  // Handle element style change from PropertiesPanel
  // Uses functional state update to avoid stale closure issues
  const handleElementStyleChange = useCallback((nodePath, property, value, changeType) => {
    setTemplateJSON(prev => {
      if (!prev) return prev;

      // If it's an attribute change (like href, target)
      if (changeType === 'attribute') {
        const newJSON = deepClone(prev);
        const node = findNodeByPath(newJSON, nodePath);
        if (node) {
          if (!node.attributes) node.attributes = {};
          node.attributes[property] = value;
          undoManager.current?.push(newJSON, `Change ${property}`);
          setSelectedElement(sel => sel ? { ...sel, structure: node } : null);
          return newJSON;
        }
        return prev;
      }

      const newJSON = updateNodeStyle(prev, nodePath, property, value);
      undoManager.current?.push(newJSON, `Style: ${property}`);
      const updatedNode = findNodeByPath(newJSON, nodePath);
      if (updatedNode) {
        setSelectedElement(sel => sel ? { ...sel, structure: updatedNode } : null);
      }
      return newJSON;
    });
    setIsDirty(true);
  }, []);

  // Handle block reordering
  const handleBlockReorder = useCallback((fromIndex, toIndex) => {
    setTemplateJSON(prev => {
      if (!prev) return prev;
      const newJSON = deepClone(prev);
      const blocks = [...newJSON.blocks];
      const [moved] = blocks.splice(fromIndex, 1);
      blocks.splice(toIndex, 0, moved);
      blocks.forEach((block, i) => { block.position = i + 1; });
      newJSON.blocks = blocks;
      undoManager.current?.push(newJSON, 'Reorder blocks');
      return newJSON;
    });
    setIsDirty(true);
  }, []);

  // Handle block visibility toggle
  const handleBlockToggle = useCallback((index) => {
    setTemplateJSON(prev => {
      if (!prev) return prev;
      const newJSON = deepClone(prev);
      newJSON.blocks[index].enabled = !newJSON.blocks[index].enabled;
      undoManager.current?.push(newJSON, 'Toggle block visibility');
      return newJSON;
    });
    setIsDirty(true);
  }, []);

  // Handle block duplication
  const handleBlockDuplicate = useCallback((index) => {
    setTemplateJSON(prev => {
      if (!prev) return prev;
      const newJSON = deepClone(prev);
      const original = newJSON.blocks[index];
      const clone = deepClone(original);
      clone.id = (original.id || 'section') + '-copy-' + Date.now();
      clone.position = original.position + 1;
      newJSON.blocks.splice(index + 1, 0, clone);
      newJSON.blocks.forEach((b, i) => { b.position = i + 1; });
      undoManager.current?.push(newJSON, 'Duplicate section: ' + (original.id || index));
      return newJSON;
    });
    setIsDirty(true);
  }, []);

  // Handle block deletion — uses templateJSONRef to read label without stale closure
  const templateJSONRef = useRef(null);
  templateJSONRef.current = templateJSON;

  const handleBlockDelete = useCallback((index) => {
    const cur = templateJSONRef.current;
    if (!cur) return;
    const rawBlockId = cur.blocks[index]?.id || ('Section ' + (index + 1));
    const blockLabel = rawBlockId.charAt(0).toUpperCase() + rawBlockId.slice(1).replace(/[-_]/g, ' ');
    Swal.fire({
      title: 'Delete This Section?',
      text: 'Remove "' + blockLabel + '" from your page? You can bring it back with Ctrl+Z.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        setTemplateJSON(prev => {
          if (!prev) return prev;
          const newJSON = deepClone(prev);
          newJSON.blocks.splice(index, 1);
          newJSON.blocks.forEach((b, i) => { b.position = i + 1; });
          undoManager.current?.push(newJSON, 'Delete section: ' + blockLabel);
          return newJSON;
        });
        setIsDirty(true);
      }
    });
  }, []);

  // Handle adding a new section after a given index
  const handleBlockAdd = useCallback((afterIndex) => {
    // Open the section library panel - sections get appended, which is fine
    // since user can then reorder with drag & drop
    setShowSectionLibrary(true);
    // Close other panels
    setShowThemePanel(false);
    setShowBusinessPanel(false);
    setShowVersionHistory(false);
  }, []);

  // Page switching — uses handleSaveRef to avoid hook ordering issues
  const handlePageChange = useCallback((newPage) => {
    const doSwitch = () => {
      navbarDirtyRef.current = false;
      footerDirtyRef.current = false;
      setSearchParams({ template: templateNo, page: newPage });
    };

    if (isDirty) {
      Swal.fire({
        title: 'Unsaved Changes',
        text: 'You have unsaved changes. Save before switching pages?',
        icon: 'warning',
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonText: 'Save & Switch',
        confirmButtonColor: '#111827',
        denyButtonText: 'Switch Without Saving',
        denyButtonColor: '#6b7280',
        cancelButtonText: 'Cancel'
      }).then(async (result) => {
        if (result.isConfirmed) {
          if (handleSaveRef.current) {
            await handleSaveRef.current({ silent: true });
          }
          doSwitch();
        } else if (result.isDenied) {
          setIsDirty(false);
          doSwitch();
        }
      });
    } else {
      doSwitch();
    }
  }, [isDirty, templateNo, setSearchParams]);

  // Undo/Redo
  const handleUndo = useCallback(() => {
    const desc = undoManager.current?.getDescription() || 'last change';
    const state = undoManager.current?.undo();
    if (state) {
      setTemplateJSON(state);
      setIsDirty(true);
      setUndoToast({ action: 'Undo', description: desc });
      setTimeout(() => setUndoToast(null), 2000);
    }
  }, []);

  const handleRedo = useCallback(() => {
    const state = undoManager.current?.redo();
    if (state) {
      setTemplateJSON(state);
      setIsDirty(true);
      const desc = undoManager.current?.getDescription() || 'last change';
      setUndoToast({ action: 'Redo', description: desc });
      setTimeout(() => setUndoToast(null), 2000);
    }
  }, []);

  // Save navbar/footer config to backend if modified from a non-layout page
  const saveNavbarFooterIfDirty = useCallback(async () => {
    const csrfToken = Cookies.get('csrftoken');
    const headers = {
      Authorization: `Bearer ${accessToken}`,
      'X-CSRFToken': csrfToken,
      'Content-Type': 'multipart/form-data'
    };

    if (navbarDirtyRef.current && navbarAttrs) {
      try {
        const navJSON = {
          version: '2.0',
          page_type: 'navbar',
          template_id: templateNo,
          blocks: [{
            id: 'navbar',
            type: 'section',
            position: 1,
            enabled: true,
            structure: { type: 'NavbarWrapper', attributes: navbarAttrs, children: [] }
          }],
          content: {},
          theme: masterThemeRef.current || {}
        };
        const fd = new FormData();
        fd.append('temp_no', templateNo);
        fd.append('template_json', JSON.stringify(navJSON));
        await axios.post(
          `${API_BASE_URL}/api/template/${templateNo}/page/navbar/save`,
          fd,
          { headers }
        );
        navbarDirtyRef.current = false;
      } catch (err) {
        console.error('Failed to save navbar config:', err);
      }
    }

    if (footerDirtyRef.current && footerAttrs) {
      try {
        const footJSON = {
          version: '2.0',
          page_type: 'footer',
          template_id: templateNo,
          blocks: [{
            id: 'footer',
            type: 'section',
            position: 1,
            enabled: true,
            structure: { type: 'FooterWrapper', attributes: footerAttrs, children: [] }
          }],
          content: {},
          theme: masterThemeRef.current || {}
        };
        const fd = new FormData();
        fd.append('temp_no', templateNo);
        fd.append('template_json', JSON.stringify(footJSON));
        await axios.post(
          `${API_BASE_URL}/api/template/${templateNo}/page/footer/save`,
          fd,
          { headers }
        );
        footerDirtyRef.current = false;
      } catch (err) {
        console.error('Failed to save footer config:', err);
      }
    }
  }, [accessToken, templateNo, navbarAttrs, footerAttrs]);

  // Save to server
  // silent: if true, skip the success modal (used during page-switch save)
  const handleSave = useCallback(async (options = {}) => {
    const { silent = false } = typeof options === 'object' ? options : {};
    if (!templateJSON || !accessToken || isSaving) return;
    setIsSaving(true);

    try {
      const submitData = new FormData();
      submitData.append('temp_no', templateNo);
      submitData.append('temp_name', templateJSON.template_name || `Template ${templateNo}`);
      submitData.append('template_json', JSON.stringify(templateJSON));

      const brand = templateJSON.content?.brand;
      if (brand?.name) submitData.append('brand_name', brand.name);

      const contact = templateJSON.content?.contact;
      if (contact?.email) submitData.append('contact_email', contact.email);
      if (contact?.phone) submitData.append('contact_phone', contact.phone);
      if (contact?.address) submitData.append('contact_address', contact.address);

      const social = templateJSON.content?.social;
      if (social) {
        if (social.instagram) submitData.append('social_instagram', social.instagram);
        if (social.facebook) submitData.append('social_facebook', social.facebook);
        if (social.twitter) submitData.append('social_twitter', social.twitter);
        if (social.youtube) submitData.append('social_youtube', social.youtube);
        if (social.linkedin) submitData.append('social_linkedin', social.linkedin);
      }

      // Append files and build a mapping of backend field names to original content paths
      const fileBindings = {};
      Object.entries(pendingFiles).forEach(([fieldPath, file]) => {
        const backendField = mapFieldToBackend(fieldPath);
        submitData.append(backendField, file);
        fileBindings[backendField] = fieldPath;
      });
      if (Object.keys(fileBindings).length > 0) {
        submitData.append('_file_bindings', JSON.stringify(fileBindings));
      }

      const csrfToken = Cookies.get('csrftoken');

      await axios.post(
        `${API_BASE_URL}/api/template/${templateNo}/page/${pageType}/save`,
        submitData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'X-CSRFToken': csrfToken,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Also save navbar/footer config if modified from a non-layout page
      const isLayoutPage = pageType === 'navbar' || pageType === 'footer';
      if (!isLayoutPage) {
        await saveNavbarFooterIfDirty();
      }

      setIsDirty(false);
      setPendingFiles({});
      // Clear local draft on save
      try {
        localStorage.removeItem(`editor_draft_${templateNo}_${pageType}`);
      } catch (e) {}

      if (silent) {
        // Silent save — just show a brief toast
        setAutoSaveStatus('saved');
        setTimeout(() => setAutoSaveStatus(null), 2000);
        return;
      }

      // After save — offer preview and dashboard navigation
      const firstSaveKey = `editor_first_save_${templateNo}`;
      const isFirstSave = !localStorage.getItem(firstSaveKey);
      if (isFirstSave) localStorage.setItem(firstSaveKey, Date.now().toString());

      Swal.fire({
        icon: 'success',
        title: isFirstSave ? 'Your website is live!' : 'Changes saved successfully!',
        html: isFirstSave
          ? `<p style="color:#64748b;font-size:14px;margin-top:4px">Your first design is saved and ready for your customers to see.</p>${user?.username ? `<p style="margin-top:8px"><a href="http://${user.username}.vasstr.com" target="_blank" style="color:#4f46e5;font-weight:600;text-decoration:underline">View your live site</a></p>` : ''}`
          : `<p style="color:#64748b;font-size:14px;margin-top:4px">Your changes are now live on your website.</p>${user?.username ? `<p style="margin-top:8px"><a href="http://${user.username}.vasstr.com" target="_blank" style="color:#4f46e5;font-weight:600;text-decoration:underline">View your live site</a></p>` : ''}`,
        showConfirmButton: true,
        confirmButtonText: user?.username ? 'Open My Website' : 'Continue Editing',
        confirmButtonColor: '#4f46e5',
        showDenyButton: true,
        denyButtonText: 'Back to Dashboard',
        denyButtonColor: '#111827',
        showCancelButton: true,
        cancelButtonText: 'Continue Editing',
        backdrop: 'rgba(0,0,0,0.4)',
        customClass: { popup: 'shadow-2xl rounded-2xl' },
        showClass: { popup: 'animate__animated animate__fadeInUp animate__faster' },
        hideClass: { popup: 'animate__animated animate__fadeOutDown animate__faster' },
      }).then((result) => {
        if (result.isConfirmed) {
          if (user?.username) {
            // Open the live website in a new tab
            window.open(`http://${user.username}.vasstr.com`, '_blank');
          }
        } else if (result.isDenied) {
          // Go to dashboard
          navigate(`/template-builder/${templateNo}`);
        }
        // Cancel = continue editing, do nothing
      });
    } catch (err) {
      console.error('Save error:', err);
      if (!silent) {
        Swal.fire({
          icon: 'error',
          title: 'Unable to save your changes',
          html: '<p style="color:#64748b;font-size:14px;margin-top:4px">Don\'t worry -- your work is safely saved on this device. This can happen if your internet connection dropped or our servers are temporarily busy.</p>',
          confirmButtonText: 'Try Again',
          confirmButtonColor: '#111827',
          showCancelButton: true,
          cancelButtonText: 'Keep Editing',
        }).then((result) => {
          if (result.isConfirmed) handleSave();
        });
      }
    } finally {
      setIsSaving(false);
    }
  }, [templateJSON, accessToken, templateNo, pageType, isSaving, pendingFiles, navigate, saveNavbarFooterIfDirty]);

  // Keep ref in sync so handlePageChange can call it without circular dependency
  handleSaveRef.current = handleSave;

  // Save draft to server (without publishing)
  const handleSaveDraft = useCallback(async () => {
    if (!templateJSON || isDraftSaving) return;
    setIsDraftSaving(true);
    try {
      const csrfToken = Cookies.get('csrftoken');
      await axios.post(
        `${API_BASE_URL}/api/template-drafts/save/`,
        {
          temp_no: templateNo,
          draft_data: templateJSON,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'X-CSRFToken': csrfToken,
          }
        }
      );
      // Also save locally
      try {
        localStorage.setItem(`editor_draft_${templateNo}_${pageType}`, JSON.stringify({
          json: templateJSON,
          timestamp: Date.now(),
        }));
      } catch (e) {}

      setAutoSaveStatus('saved');
      setTimeout(() => setAutoSaveStatus(null), 2000);
    } catch (err) {
      console.error('Draft save error:', err);
      // Silent fail for drafts — still save locally
      try {
        localStorage.setItem(`editor_draft_${templateNo}_${pageType}`, JSON.stringify({
          json: templateJSON,
          timestamp: Date.now(),
        }));
      } catch (e) {}
    } finally {
      setIsDraftSaving(false);
    }
  }, [templateJSON, isDraftSaving, templateNo, pageType, accessToken]);

  // Autosave draft every 30 seconds (saves to draft, not live)
  useEffect(() => {
    if (autoSaveTimerRef.current) clearInterval(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setInterval(() => {
      if (isDirty && !isSaving && !isDraftSaving && templateJSON && accessToken) {
        setAutoSaveStatus('saving');
        handleSaveDraft().then(() => {
          setAutoSaveStatus('saved');
          setTimeout(() => setAutoSaveStatus(null), 3000);
        }).catch(() => {
          setAutoSaveStatus(null);
        });
      }
    }, 30000);
    return () => {
      if (autoSaveTimerRef.current) clearInterval(autoSaveTimerRef.current);
    };
  }, [isDirty, isSaving, isDraftSaving, handleSaveDraft, templateJSON, accessToken]);

  // Toolbar actions (must be defined before keyboard shortcuts useEffect that references it)
  const handleToolbarAction = useCallback((action, value) => {
    if (!selectedElement) return;
    switch (action) {
      case 'bold':
        document.execCommand('bold', false, null);
        break;
      case 'italic':
        document.execCommand('italic', false, null);
        break;
      case 'underline':
        document.execCommand('underline', false, null);
        break;
      case 'strikethrough':
        document.execCommand('strikeThrough', false, null);
        break;
      case 'align-left':
      case 'align-center':
      case 'align-right':
        if (selectedElement.domNode) {
          selectedElement.domNode.style.textAlign = action.replace('align-', '');
        }
        break;
      case 'font-size-increase':
      case 'font-size-decrease': {
        if (selectedElement.domNode) {
          const current = parseFloat(window.getComputedStyle(selectedElement.domNode).fontSize) || 16;
          const delta = action === 'font-size-increase' ? 2 : -2;
          const newSize = Math.max(8, Math.min(120, current + delta));
          if (selectedElement.nodePath) {
            handleElementStyleChange(selectedElement.nodePath, 'fontSize', `${newSize}px`);
          }
        }
        break;
      }
      case 'text-color':
        if (value && selectedElement.nodePath) {
          handleElementStyleChange(selectedElement.nodePath, 'color', value);
        }
        break;
      case 'link': {
        const currentHref = selectedElement?.structure?.attributes?.href || selectedElement?.structure?.href || '';
        Swal.fire({
          title: 'Edit Link',
          input: 'url',
          inputValue: currentHref,
          inputPlaceholder: 'https://example.com',
          showCancelButton: true,
          confirmButtonText: 'Apply',
          confirmButtonColor: '#4f46e5',
          inputValidator: (value) => {
            if (value && !value.match(/^(https?:\/\/|\/|#|mailto:|tel:)/)) {
              return 'Please enter a valid URL';
            }
          }
        }).then((result) => {
          if (result.isConfirmed && selectedElement?.nodePath) {
            setTemplateJSON(prev => {
              if (!prev) return prev;
              const newJSON = deepClone(prev);
              const node = findNodeByPath(newJSON, selectedElement.nodePath);
              if (node) {
                if (!node.attributes) node.attributes = {};
                node.attributes.href = result.value || '';
                if (result.value) {
                  node.attributes.target = node.attributes.target || '_blank';
                }
                undoManager.current?.push(newJSON, 'Edit link');
                return newJSON;
              }
              return prev;
            });
            setIsDirty(true);
          }
        });
        break;
      }
      case 'replace-image':
        if (selectedElement.domNode) {
          selectedElement.domNode.click();
        }
        break;
      case 'opacity':
        if (value !== undefined && selectedElement.nodePath) {
          handleElementStyleChange(selectedElement.nodePath, 'opacity', value);
        }
        break;
      case 'border-radius':
        if (value && selectedElement.nodePath) {
          handleElementStyleChange(selectedElement.nodePath, 'borderRadius', value);
        }
        break;
      case 'background-color':
        if (value && selectedElement.nodePath) {
          handleElementStyleChange(selectedElement.nodePath, 'backgroundColor', value);
        }
        break;
      case 'copy-style': {
        if (selectedElement.domNode) {
          const cs = window.getComputedStyle(selectedElement.domNode);
          setCopiedStyle({
            color: cs.color,
            backgroundColor: cs.backgroundColor,
            fontSize: cs.fontSize,
            fontWeight: cs.fontWeight,
            fontFamily: cs.fontFamily,
            lineHeight: cs.lineHeight,
            letterSpacing: cs.letterSpacing,
            textAlign: cs.textAlign,
            borderRadius: cs.borderRadius,
            opacity: cs.opacity,
            padding: cs.padding,
          });
          setUndoToast({ action: 'Copied', description: 'Style copied to clipboard' });
          setTimeout(() => setUndoToast(null), 1500);
        }
        break;
      }
      case 'paste-style': {
        if (copiedStyle && selectedElement.nodePath) {
          setTemplateJSON(prev => {
            if (!prev) return prev;
            const newJSON = deepClone(prev);
            const node = findNodeByPath(newJSON, selectedElement.nodePath);
            if (node) {
              if (!node.style) node.style = {};
              Object.entries(copiedStyle).forEach(([k, v]) => {
                if (v && v !== 'normal' && v !== 'rgba(0, 0, 0, 0)') {
                  node.style[k] = v;
                }
              });
              undoManager.current?.push(newJSON, 'Paste style');
              return newJSON;
            }
            return prev;
          });
          setIsDirty(true);
          setUndoToast({ action: 'Pasted', description: 'Style applied' });
          setTimeout(() => setUndoToast(null), 1500);
        }
        break;
      }
      case 'duplicate': {
        if (selectedElement.nodePath) {
          const pathParts = selectedElement.nodePath.split('.');
          if (pathParts.length >= 2) {
            const parentPath = pathParts.slice(0, -1).join('.');
            const childIndex = parseInt(pathParts[pathParts.length - 1], 10);
            setTemplateJSON(prev => {
              if (!prev) return prev;
              const newJSON = deepClone(prev);
              const parentNode = findNodeByPath(newJSON, parentPath);
              if (Array.isArray(parentNode) && !isNaN(childIndex) && childIndex < parentNode.length) {
                const cloned = deepClone(parentNode[childIndex]);
                parentNode.splice(childIndex + 1, 0, cloned);
                undoManager.current?.push(newJSON, 'Duplicate element');
                return newJSON;
              }
              return prev;
            });
            setIsDirty(true);
            setUndoToast({ action: 'Duplicated', description: 'Element duplicated' });
            setTimeout(() => setUndoToast(null), 1500);
          }
        }
        break;
      }
      case 'move-up': {
        if (selectedElement.nodePath) {
          const pathParts = selectedElement.nodePath.split('.');
          if (pathParts.length >= 2) {
            const parentPath = pathParts.slice(0, -1).join('.');
            const childIndex = parseInt(pathParts[pathParts.length - 1], 10);
            if (childIndex > 0) {
              setTemplateJSON(prev => {
                if (!prev) return prev;
                const newJSON = deepClone(prev);
                const parentNode = findNodeByPath(newJSON, parentPath);
                if (Array.isArray(parentNode)) {
                  const [moved] = parentNode.splice(childIndex, 1);
                  parentNode.splice(childIndex - 1, 0, moved);
                  undoManager.current?.push(newJSON, 'Move element up');
                  return newJSON;
                }
                return prev;
              });
              setIsDirty(true);
            }
          }
        }
        break;
      }
      case 'move-down': {
        if (selectedElement.nodePath) {
          const pathParts = selectedElement.nodePath.split('.');
          if (pathParts.length >= 2) {
            const parentPath = pathParts.slice(0, -1).join('.');
            const childIndex = parseInt(pathParts[pathParts.length - 1], 10);
            setTemplateJSON(prev => {
              if (!prev) return prev;
              const newJSON = deepClone(prev);
              const parentNode = findNodeByPath(newJSON, parentPath);
              if (Array.isArray(parentNode) && childIndex < parentNode.length - 1) {
                const [moved] = parentNode.splice(childIndex, 1);
                parentNode.splice(childIndex + 1, 0, moved);
                undoManager.current?.push(newJSON, 'Move element down');
                return newJSON;
              }
              return prev;
            });
            setIsDirty(true);
          }
        }
        break;
      }
      case 'delete':
        if (selectedElement.nodePath) {
          const pathParts = selectedElement.nodePath.split('.');
          if (pathParts.length >= 2) {
            const parentPath = pathParts.slice(0, -1).join('.');
            const childIndex = parseInt(pathParts[pathParts.length - 1], 10);
            setTemplateJSON(prev => {
              if (!prev) return prev;
              const newJSON = deepClone(prev);
              const parentNode = findNodeByPath(newJSON, parentPath);
              if (Array.isArray(parentNode) && !isNaN(childIndex) && childIndex < parentNode.length) {
                parentNode.splice(childIndex, 1);
                undoManager.current?.push(newJSON, 'Delete element');
                return newJSON;
              }
              return prev;
            });
            setIsDirty(true);
          }
          setSelectedElement(null);
          setEditingNodePath(null);
          setShowPropertiesPanel(false);
        }
        break;
      case 'dismiss':
        setSelectedElement(null);
        setEditingNodePath(null);
        setShowPropertiesPanel(false);
        break;
      default:
        break;
    }
  }, [selectedElement, handleElementStyleChange, copiedStyle]);

  // Keyboard shortcuts — skip undo/redo when user is typing in a contentEditable element
  useEffect(() => {
    const handleKeyDown = (e) => {
      const isEditing = document.activeElement?.isContentEditable;
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
        return;
      }
      // Zoom controls: Ctrl+=/+, Ctrl+-, Ctrl+0
      if ((e.ctrlKey || e.metaKey) && (e.key === '=' || e.key === '+')) {
        e.preventDefault();
        setZoomLevel(prev => Math.min(200, prev + 25));
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key === '-') {
        e.preventDefault();
        setZoomLevel(prev => Math.max(25, prev - 25));
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key === '0') {
        e.preventDefault();
        setZoomLevel(100);
        return;
      }

      // Ctrl+D: duplicate selected element
      if ((e.ctrlKey || e.metaKey) && e.key === 'd' && selectedElement && !isEditing) {
        e.preventDefault();
        handleToolbarAction('duplicate');
        return;
      }

      // Escape key: exit text edit → deselect
      if (e.key === 'Escape') {
        if (editingNodePath) {
          setEditingNodePath(null);
        } else if (selectedElement) {
          setSelectedElement(null);
          setShowPropertiesPanel(false);
        }
        return;
      }

      // Delete key: remove selected element (but not while editing text)
      if ((e.key === 'Delete' || e.key === 'Backspace') && !isEditing && selectedElement && !editingNodePath) {
        e.preventDefault();
        if (selectedElement.nodePath) {
          const pathParts = selectedElement.nodePath.split('.');
          if (pathParts.length >= 2) {
            const parentPath = pathParts.slice(0, -1).join('.');
            const childIndex = parseInt(pathParts[pathParts.length - 1], 10);
            setTemplateJSON(prev => {
              if (!prev) return prev;
              const newJSON = deepClone(prev);
              const parentNode = findNodeByPath(newJSON, parentPath);
              if (Array.isArray(parentNode) && !isNaN(childIndex) && childIndex < parentNode.length) {
                parentNode.splice(childIndex, 1);
                undoManager.current?.push(newJSON, 'Delete element');
                return newJSON;
              }
              return prev;
            });
            setIsDirty(true);
          }
          setSelectedElement(null);
          setEditingNodePath(null);
          setShowPropertiesPanel(false);
        }
        return;
      }

      // Don't intercept undo/redo while editing text — let the browser handle it natively
      if (isEditing) return;
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        handleRedo();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSave, handleUndo, handleRedo, handleToolbarAction, editingNodePath, selectedElement]);

  // Ctrl+scroll wheel zoom on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const handleWheel = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -10 : 10;
        setZoomLevel(prev => Math.max(25, Math.min(200, prev + delta)));
      }
    };
    canvas.addEventListener('wheel', handleWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', handleWheel);
  }, []);

  // Map frontend binding paths to backend field names
  // The backend's process_template_files_for_v2 expects specific field naming patterns
  const mapFieldToBackend = (fieldPath) => {
    // Direct content path mappings
    if (fieldPath === 'content.brand.logo') return 'brand_logo';
    if (fieldPath === 'content.hero.background_image' || fieldPath === 'content.hero.backgroundImage') return 'hero_background';
    if (fieldPath === 'content.hero.image') return 'hero_background';
    if (fieldPath === 'content.featuredImage.src') return 'featuredImage';
    if (fieldPath === 'content.philosophy.image') return 'philosophy_image';
    if (fieldPath === 'content.craftsmanship.image') return 'craftsmanship_image';
    if (fieldPath === 'content.editorial.image') return 'editorial_image';
    if (fieldPath === 'content.materials.image1') return 'materials_image1';
    if (fieldPath === 'content.materials.image2') return 'materials_image2';
    if (fieldPath === 'content.brandStory.image') return 'brandStory_image';
    if (fieldPath === 'content.brand_story.image') return 'brand_story_image';
    if (fieldPath === 'content.brandStatement.image') return 'brandStatement_image';
    if (fieldPath === 'content.mission.image') return 'mission_image';
    if (fieldPath === 'content.videoBanner.image') return 'video_banner_image';
    if (fieldPath === 'content.videoCampaign.image') return 'video_campaign_image';
    if (fieldPath === 'content.videoLookbook.image') return 'video_lookbook_image';
    if (fieldPath === 'content.theEdit.image') return 'the_edit_image';
    if (fieldPath === 'content.athlete.backgroundImage') return 'athlete_image';
    if (fieldPath === 'content.consultation.image') return 'consultation_image';
    if (fieldPath === 'content.video.image') return 'video_image';
    if (fieldPath === 'content.craftsmanship.background_image') return 'craftsmanship_bg_image';
    if (fieldPath === 'content.craftsmanship.mobileImage') return 'craftsmanship_mobile_image';
    if (fieldPath === 'content.editorial.backgroundImage') return 'editorial_bg_image';
    if (fieldPath === 'content.editorial.activeItem.image') return 'editorial_activeItem_image';
    if (fieldPath === 'content.behindScenes.image') return 'behindScenes_image';
    if (fieldPath === 'content.lookbook.featured_image') return 'lookbook_featured_image';

    // Indexed content path mappings (arrays)
    // content.categories.N.image → category_image_N
    const categoryMatch = fieldPath.match(/^content\.categories\.(?:items\.)?(\d+)\.image$/);
    if (categoryMatch) return `category_image_${categoryMatch[1]}`;
    // content.collections.N.image → collection_image_N
    const collectionMatch = fieldPath.match(/^content\.collections\.(?:items\.)?(\d+)\.image$/);
    if (collectionMatch) return `collection_image_${collectionMatch[1]}`;
    // content.gallery.N.image → gallery_image_N
    const galleryMatch = fieldPath.match(/^content\.gallery\.(\d+)\.image$/);
    if (galleryMatch) return `gallery_image_${parseInt(galleryMatch[1]) + 1}`;
    // content.instagram.posts.N.image → instagram_post_N
    const instagramMatch = fieldPath.match(/^content\.instagram\.(?:posts\.)?(\d+)\.image$/);
    if (instagramMatch) return `instagram_post_${instagramMatch[1]}`;
    // content.testimonials.items.N.image → testimonial_avatar_N
    const testimonialMatch = fieldPath.match(/^content\.testimonials\.(?:items\.)?(\d+)\.image$/);
    if (testimonialMatch) return `testimonial_avatar_${testimonialMatch[1]}`;
    // content.lookbook.items.N.image → lookbook_image_N
    const lookbookMatch = fieldPath.match(/^content\.lookbook\.(?:items\.)?(\d+)\.image$/);
    if (lookbookMatch) return `lookbook_image_${lookbookMatch[1]}`;
    // content.features.items.N.image → feature_image_N
    const featureMatch = fieldPath.match(/^content\.features\.(?:items\.)?(\d+)\.image$/);
    if (featureMatch) return `feature_image_${featureMatch[1]}`;
    // content.services.items.N.image → service_image_N
    const serviceMatch = fieldPath.match(/^content\.services\.(?:items\.)?(\d+)\.image$/);
    if (serviceMatch) return `service_image_${serviceMatch[1]}`;
    // content.trending.items.N.image → trending_image_N
    const trendingMatch = fieldPath.match(/^content\.trending\.(?:items\.)?(\d+)\.image$/);
    if (trendingMatch) return `trending_image_${trendingMatch[1]}`;
    // content.portfolio.items.N.image → portfolio_image_N
    const portfolioMatch = fieldPath.match(/^content\.portfolio\.(?:items\.)?(\d+)\.image$/);
    if (portfolioMatch) return `portfolio_image_${portfolioMatch[1]}`;
    // content.designers.items.N.image → designer_image_N
    const designerMatch = fieldPath.match(/^content\.designers\.(?:items\.)?(\d+)\.image$/);
    if (designerMatch) return `designer_image_${designerMatch[1]}`;
    // content.cityCollections.items.N.image → city_collection_image_N
    const cityMatch = fieldPath.match(/^content\.cityCollections\.(?:items\.)?(\d+)\.image$/);
    if (cityMatch) return `city_collection_image_${cityMatch[1]}`;
    // content.styleGuide.items.N.image → style_guide_image_N
    const styleGuideMatch = fieldPath.match(/^content\.styleGuide\.(?:items\.)?(\d+)\.image$/);
    if (styleGuideMatch) return `style_guide_image_${styleGuideMatch[1]}`;
    // content.featuredBrands.items.N.image → featured_brand_image_N
    const featuredBrandMatch = fieldPath.match(/^content\.featuredBrands\.(?:items\.)?(\d+)\.image$/);
    if (featuredBrandMatch) return `featured_brand_image_${featuredBrandMatch[1]}`;
    // content.reels.items.N.image → reel_image_N
    const reelMatch = fieldPath.match(/^content\.reels\.(?:items\.)?(\d+)\.image$/);
    if (reelMatch) return `reel_image_${reelMatch[1]}`;
    // content.hero.collections.N.image → hero_collection_image_N
    const heroCollMatch = fieldPath.match(/^content\.hero\.collections\.(\d+)\.image$/);
    if (heroCollMatch) return `hero_collection_image_${heroCollMatch[1]}`;
    // content.promoBanners.N.image → promo_banner_image_N
    const promoMatch = fieldPath.match(/^content\.promoBanners\.(\d+)\.image$/);
    if (promoMatch) return `promo_banner_image_${promoMatch[1]}`;
    // content.intro.certifications.N.image → certification_image_N
    const certMatch = fieldPath.match(/^content\.intro\.certifications\.(\d+)\.image$/);
    if (certMatch) return `certification_image_${certMatch[1]}`;
    // content.videos.items.N.thumbnail → video_thumbnail_N
    const videoThumbMatch = fieldPath.match(/^content\.videos\.(?:items\.)?(\d+)\.thumbnail$/);
    if (videoThumbMatch) return `video_thumbnail_${videoThumbMatch[1]}`;

    // Generic fallback: strip "content." prefix and replace dots with underscores
    let cleaned = fieldPath;
    if (cleaned.startsWith('content.')) cleaned = cleaned.slice(8);
    return cleaned.replace(/\./g, '_');
  };

  // Preview dimensions based on device
  const previewStyle = useMemo(() => {
    switch (previewDevice) {
      case 'mobile':
        return { width: '375px', maxWidth: '375px' };
      case 'tablet':
        return { width: '768px', maxWidth: '768px' };
      default:
        return { width: '100%', maxWidth: '100%' };
    }
  }, [previewDevice]);

  // Close panels when switching to the other
  const toggleThemePanel = useCallback(() => {
    setShowThemePanel(prev => !prev);
    if (!showThemePanel) { setShowBusinessPanel(false); setShowSectionLibrary(false); setShowVersionHistory(false); setShowAnimationPanel(false); setActiveConfigPanel(null); }
  }, [showThemePanel]);

  const toggleBusinessPanel = useCallback(() => {
    setShowBusinessPanel(prev => !prev);
    if (!showBusinessPanel) { setShowThemePanel(false); setShowSectionLibrary(false); setShowVersionHistory(false); setShowAnimationPanel(false); setActiveConfigPanel(null); }
  }, [showBusinessPanel]);

  const toggleSectionLibrary = useCallback(() => {
    setShowSectionLibrary(prev => !prev);
    if (!showSectionLibrary) { setShowThemePanel(false); setShowBusinessPanel(false); setShowVersionHistory(false); setShowAnimationPanel(false); setActiveConfigPanel(null); }
  }, [showSectionLibrary]);

  const toggleVersionHistory = useCallback(() => {
    setShowVersionHistory(prev => !prev);
    if (!showVersionHistory) { setShowThemePanel(false); setShowBusinessPanel(false); setShowSectionLibrary(false); setShowAnimationPanel(false); setActiveConfigPanel(null); }
  }, [showVersionHistory]);

  const toggleAnimationPanel = useCallback(() => {
    setShowAnimationPanel(prev => !prev);
    if (!showAnimationPanel) { setShowThemePanel(false); setShowBusinessPanel(false); setShowSectionLibrary(false); setShowVersionHistory(false); setActiveConfigPanel(null); }
  }, [showAnimationPanel]);

  // Handle block animation updates from AnimationPanel
  const handleBlockAnimationUpdate = useCallback((blockIndex, updatedBlock) => {
    setTemplateJSON(prev => {
      if (!prev) return prev;
      const newJSON = deepClone(prev);
      newJSON.blocks[blockIndex] = updatedBlock;
      undoManager.current?.push(newJSON, 'Update animation: ' + (updatedBlock.id || blockIndex));
      return newJSON;
    });
    setIsDirty(true);
  }, []);

  const handleRestoreVersion = useCallback((restoredJSON) => {
    setTemplateJSON(restoredJSON);
    setIsDirty(true);
    undoManager.current?.push(restoredJSON, 'Restore from version history');
    setShowVersionHistory(false);
    Swal.fire({ icon: 'success', title: 'Version Restored', timer: 1500, showConfirmButton: false, toast: true, position: 'top-end' });
  }, []);

  const handleAddFromLibrary = useCallback((sectionJSON) => {
    setTemplateJSON(prev => {
      if (!prev) return prev;
      const newJSON = deepClone(prev);
      sectionJSON.position = newJSON.blocks.length + 1;
      newJSON.blocks.push(sectionJSON);
      undoManager.current?.push(newJSON, 'Add section: ' + (sectionJSON.id || 'new'));
      return newJSON;
    });
    setIsDirty(true);
    // Brief toast feedback
    setUndoToast({ action: 'Added', description: (sectionJSON.id || 'section').replace(/[-_]/g, ' ').replace(/-\d+$/, '') });
    setTimeout(() => setUndoToast(null), 1500);
  }, []);

  // ── Loading state — immersive branded experience ──
  if (isLoading) {
    return (
      <div style={{
        height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#0f0f14',
        position: 'relative', overflow: 'hidden',
      }}>
        {/* Ambient glow */}
        <div style={{
          position: 'absolute', top: '30%', left: '50%', transform: 'translate(-50%, -50%)',
          width: '600px', height: '600px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(99,102,241,0.08) 0%, transparent 70%)',
          animation: 'editorPulseGlow 3s ease-in-out infinite',
        }} />
        <div style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
          {/* Animated logo mark */}
          <div style={{
            width: '64px', height: '64px', margin: '0 auto 28px',
            position: 'relative',
          }}>
            <div style={{
              position: 'absolute', inset: 0,
              border: '2px solid rgba(99,102,241,0.2)', borderTopColor: '#6366f1',
              borderRadius: '50%', animation: 'editorSpin 1s cubic-bezier(0.4, 0, 0.2, 1) infinite',
            }} />
            <div style={{
              position: 'absolute', inset: '8px',
              border: '2px solid rgba(168,85,247,0.15)', borderBottomColor: '#a855f7',
              borderRadius: '50%', animation: 'editorSpin 1.5s cubic-bezier(0.4, 0, 0.2, 1) infinite reverse',
            }} />
            <div style={{
              position: 'absolute', inset: '16px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Sparkles style={{ width: '20px', height: '20px', color: '#818cf8', animation: 'editorFadeInOut 2s ease-in-out infinite' }} />
            </div>
          </div>
          <p style={{
            fontSize: '18px', fontWeight: 600, color: '#e2e8f0',
            marginBottom: '8px', letterSpacing: '-0.02em',
            fontFamily: "'Inter', -apple-system, sans-serif",
          }}>
            Preparing your studio
          </p>
          <p style={{
            fontSize: '13px', color: '#64748b', fontWeight: 400,
            letterSpacing: '0.01em',
          }}>
            Your creative workspace is loading
          </p>
          {/* Progress dots */}
          <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', marginTop: '24px' }}>
            {[0, 1, 2].map(i => (
              <div key={i} style={{
                width: '6px', height: '6px', borderRadius: '50%',
                background: '#6366f1',
                animation: `editorDotPulse 1.2s ease-in-out ${i * 0.2}s infinite`,
              }} />
            ))}
          </div>
        </div>
        <style>{`
          @keyframes editorSpin { to { transform: rotate(360deg); } }
          @keyframes editorPulseGlow { 0%, 100% { opacity: 0.5; transform: translate(-50%, -50%) scale(1); } 50% { opacity: 1; transform: translate(-50%, -50%) scale(1.1); } }
          @keyframes editorFadeInOut { 0%, 100% { opacity: 0.4; } 50% { opacity: 1; } }
          @keyframes editorDotPulse { 0%, 100% { opacity: 0.3; transform: scale(1); } 50% { opacity: 1; transform: scale(1.3); } }
        `}</style>
      </div>
    );
  }

  // ── No template selected — elegant template picker ──
  if (!templateJSON) {
    const TEMPLATE_VIBES = [
      'Luxury Minimal', 'Bold Editorial', 'Warm Artisanal', 'Modern Contemporary', 'Heritage Classic',
      'Urban Streetwear', 'Bohemian Spirit', 'Couture Glamour', 'Eco Sustainable', 'Sport Luxe',
      'Resort Tropical', 'Avant-Garde', 'Ethnic Bridal', 'Scandinavian', 'Vintage Retro',
      'K-Fashion', 'Denim Workwear', 'Evening Cocktail', 'Kids Fashion', 'Accessories',
    ];
    const TEMPLATE_GRADIENTS = [
      ['#1a1a2e', '#16213e'], ['#0d0d0d', '#1a1a1a'], ['#2c1810', '#3d2415'], ['#0f172a', '#1e293b'], ['#1c1917', '#292524'],
      ['#18181b', '#27272a'], ['#431407', '#7c2d12'], ['#1e1b4b', '#312e81'], ['#064e3b', '#065f46'], ['#0c0a09', '#1c1917'],
      ['#134e4a', '#115e59'], ['#171717', '#262626'], ['#4c0519', '#881337'], ['#f8fafc', '#e2e8f0'], ['#44403c', '#57534e'],
      ['#fdf2f8', '#fce7f3'], ['#292524', '#44403c'], ['#1e1b4b', '#4c1d95'], ['#fef3c7', '#fde68a'], ['#1c1917', '#292524'],
    ];
    return (
      <div style={{ height: '100vh', background: '#0f0f14', display: 'flex', flexDirection: 'column' }}>
        <div style={{
          height: '64px', background: 'rgba(15,15,20,0.95)', borderBottom: '1px solid rgba(255,255,255,0.06)',
          display: 'flex', alignItems: 'center', padding: '0 24px', gap: '16px',
          backdropFilter: 'blur(12px)',
        }}>
          <button
            onClick={() => navigate('/TemplateThemes')}
            style={{
              width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.04)',
              cursor: 'pointer', color: '#94a3b8', transition: 'all 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#e2e8f0'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.color = '#94a3b8'; }}
          >
            <ArrowLeft style={{ width: '16px', height: '16px' }} />
          </button>
          <div>
            <span style={{ fontSize: '15px', fontWeight: 600, color: '#f1f5f9', letterSpacing: '-0.01em' }}>Choose Your Template</span>
            <p style={{ fontSize: '12px', color: '#64748b', marginTop: '1px' }}>Each template is fully customizable</p>
          </div>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: '40px 32px' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '20px' }}>
              {Array.from({ length: 20 }, (_, i) => i + 1).map(num => (
                <button
                  key={num}
                  onClick={() => setSearchParams({ template: num, page: 'main' })}
                  style={{
                    background: 'rgba(255,255,255,0.03)', borderRadius: '16px',
                    border: '1px solid rgba(255,255,255,0.06)',
                    padding: '0', cursor: 'pointer', textAlign: 'left',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    overflow: 'hidden',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor = 'rgba(99,102,241,0.3)';
                    e.currentTarget.style.boxShadow = '0 8px 32px rgba(99,102,241,0.15)';
                    e.currentTarget.style.transform = 'translateY(-4px)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div style={{
                    aspectRatio: '4/5',
                    background: `linear-gradient(135deg, ${TEMPLATE_GRADIENTS[num - 1]?.[0] || '#1a1a2e'}, ${TEMPLATE_GRADIENTS[num - 1]?.[1] || '#16213e'})`,
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    position: 'relative', overflow: 'hidden',
                  }}>
                    {/* Decorative grid pattern */}
                    <div style={{
                      position: 'absolute', inset: 0, opacity: 0.05,
                      backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
                      backgroundSize: '20px 20px',
                    }} />
                    <span style={{
                      fontSize: '36px', fontWeight: 700, color: 'rgba(255,255,255,0.15)',
                      fontFamily: "'Playfair Display', Georgia, serif",
                      letterSpacing: '-0.03em',
                    }}>{String(num).padStart(2, '0')}</span>
                  </div>
                  <div style={{ padding: '14px 16px' }}>
                    <p style={{
                      fontSize: '13px', fontWeight: 600, color: '#e2e8f0',
                      letterSpacing: '-0.01em', marginBottom: '2px',
                    }}>
                      {TEMPLATE_VIBES[num - 1] || `Template ${num}`}
                    </p>
                    <p style={{ fontSize: '11px', color: '#64748b' }}>Template {num}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Shared renderer props ──
  const rendererProps = {
    templateJSON,
    onContentChange: handleContentChange,
    onImageReplace: handleImageReplace,
    onBlockReorder: handleBlockReorder,
    onBlockToggle: handleBlockToggle,
    onBlockDuplicate: handleBlockDuplicate,
    onBlockDelete: handleBlockDelete,
    onBlockAdd: handleBlockAdd,
    editMode,
    previewDevice,
    imageBaseUrl: API_BASE_URL,
    onElementSelect: setSelectedElement,
    selectedNodePath: selectedElement?.nodePath || null,
    editingNodePath,
    onElementClick: handleElementClick,
    onElementDoubleClick: handleElementDoubleClick,
    onOpenConfigPanel: handleOpenConfigPanel,
    navbarData: navbarAttrs,
    footerData: footerAttrs,
  };

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#e8eaed' }}>
      {/* ── Top Bar ── */}
      <EditorTopBar
        templateName={templateName}
        canUndo={undoInfo.canUndo}
        canRedo={undoInfo.canRedo}
        onUndo={handleUndo}
        onRedo={handleRedo}
        onSave={handleSave}
        onSaveDraft={handleSaveDraft}
        isSaving={isSaving}
        isDraftSaving={isDraftSaving}
        isDirty={isDirty}
        editMode={editMode}
        onToggleEditMode={() => setEditMode(!editMode)}
        previewDevice={previewDevice}
        onSetDevice={(device) => { setPreviewDevice(device); setZoomLevel(100); }}
        zoomLevel={zoomLevel}
        onZoomChange={setZoomLevel}
        showThemePanel={showThemePanel}
        onToggleThemePanel={toggleThemePanel}
        showBusinessPanel={showBusinessPanel}
        onToggleBusinessPanel={toggleBusinessPanel}
        showSectionLibrary={showSectionLibrary}
        onToggleSectionLibrary={toggleSectionLibrary}
        showVersionHistory={showVersionHistory}
        onToggleVersionHistory={toggleVersionHistory}
        showAnimationPanel={showAnimationPanel}
        onToggleAnimationPanel={toggleAnimationPanel}
        onBack={() => navigate('/template-builder/' + templateNo)}
        onPreviewLive={user?.username ? () => window.open(`http://${user.username}.vasstr.com`, '_blank') : undefined}
        currentPage={pageType}
        onPageChange={handlePageChange}
        autoSaveStatus={autoSaveStatus}
      />

      {/* ── Contextual hints — elegant floating bar ── */}
      {editMode && !hintsDismissed && (
        <div style={{
          background: 'linear-gradient(90deg, #faf5ff 0%, #eff6ff 50%, #f0fdf4 100%)',
          borderBottom: '1px solid rgba(99,102,241,0.08)',
          padding: '7px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: '28px', flexShrink: 0, animation: 'hintSlideIn 0.4s ease-out',
        }}>
          <span style={{
            fontSize: '11px', fontWeight: 600, color: '#6366f1',
            letterSpacing: '0.04em', textTransform: 'uppercase', marginRight: '4px',
          }}>Quick tips</span>
          {[
            { icon: '✎', text: 'Click any text to edit it', color: '#7c3aed' },
            { icon: '🖼', text: 'Hover over images to replace them', color: '#2563eb' },
            { icon: '↕', text: 'Drag sections to reorder them', color: '#059669' },
            { icon: navigator.platform?.includes('Mac') ? '⌘S' : 'Ctrl+S', text: 'Save anytime', color: '#64748b' },
          ].map((hint, i) => (
            <span key={i} style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              fontSize: '11.5px', color: '#475569', fontWeight: 450,
            }}>
              <span style={{ fontSize: '12px', color: hint.color, opacity: 0.7 }}>{hint.icon}</span>
              {hint.text}
            </span>
          ))}
          <button
            onClick={() => { localStorage.setItem('editor_hints_dismissed', 'true'); setHintsDismissed(true); }}
            style={{
              background: 'rgba(0,0,0,0.04)', border: 'none', cursor: 'pointer',
              color: '#94a3b8', fontSize: '14px', lineHeight: 1, padding: '3px 8px', borderRadius: '6px',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = '#475569'; e.currentTarget.style.background = 'rgba(0,0,0,0.08)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#94a3b8'; e.currentTarget.style.background = 'rgba(0,0,0,0.04)'; }}
            title="Got it"
          >&times;</button>
        </div>
      )}

      {/* ── Breadcrumb bar — shows element hierarchy when selected ── */}
      {/* Wrapper div stops mousedown propagation so FloatingToolbar dismiss handler doesn't fire */}
      {editMode && selectedElement && (
        <div onMouseDown={(e) => e.stopPropagation()}>
          <BreadcrumbBar
            selectedElement={selectedElement}
            templateJSON={templateJSON}
            onSelectAncestor={(ancestorPath) => {
              const ancestorNode = findNodeByPath(templateJSON, ancestorPath);
              if (ancestorNode) {
                setSelectedElement(prev => ({
                  ...prev,
                  nodePath: ancestorPath,
                  structure: ancestorNode,
                  type: 'element',
                }));
                setEditingNodePath(null);
              }
            }}
            onDeselect={() => {
              setSelectedElement(null);
              setEditingNodePath(null);
              setShowPropertiesPanel(false);
            }}
          />
        </div>
      )}

      {/* ── Main editor area ── */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* ── Canvas ── */}
        <div
          ref={canvasRef}
          onClick={(e) => {
            // Click on canvas background (not on any element) → deselect
            if (e.target === e.currentTarget || e.target === canvasRef.current) {
              setSelectedElement(null);
              setEditingNodePath(null);
              setShowPropertiesPanel(false);
            }
          }}
          style={{
            flex: 1, overflow: 'auto', position: 'relative',
            transition: 'background 0.3s ease',
            background: previewDevice !== 'desktop'
              ? '#e2e4e8'
              : '#e5e7eb',
            backgroundImage: previewDevice !== 'desktop'
              ? 'radial-gradient(circle, #c8cad0 0.6px, transparent 0.6px)'
              : 'none',
            backgroundSize: previewDevice !== 'desktop' ? '20px 20px' : undefined,
          }}
        >
          <div style={{
            maxWidth: previewStyle.maxWidth, width: previewStyle.width,
            margin: '0 auto', transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
            padding: previewDevice !== 'desktop' ? '24px 0' : '0',
            zoom: zoomLevel !== 100 ? zoomLevel / 100 : undefined,
          }}>

            {/* ── Device frame (mobile/tablet) ── */}
            {previewDevice !== 'desktop' && (
              <div style={{ width: previewStyle.width, margin: '0 auto', transition: 'all 0.5s ease' }}>
                {/* Device label */}
                <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                  <span style={{
                    fontSize: '11px', color: '#71717a', fontWeight: 500, letterSpacing: '0.03em',
                    background: 'rgba(255,255,255,0.8)', padding: '4px 14px', borderRadius: '8px',
                    backdropFilter: 'blur(8px)',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                    textTransform: 'uppercase',
                  }}>
                    {previewDevice === 'mobile' ? '375 × 812 — iPhone' : '768 × 1024 — iPad'}
                  </span>
                </div>
                {/* Status bar */}
                <div style={{
                  background: '#18181b', borderRadius: '24px 24px 0 0',
                  padding: '10px 24px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  boxShadow: '0 -2px 20px rgba(0,0,0,0.15)',
                }}>
                  <span style={{ fontSize: '12px', color: '#a1a1aa', fontWeight: 600 }}>9:41</span>
                  <div style={{ width: previewDevice === 'mobile' ? '80px' : '100px', height: '26px', background: '#09090b', borderRadius: '13px' }} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <div style={{ width: '15px', height: '11px', border: '1.5px solid #71717a', borderRadius: '2.5px', position: 'relative' }}>
                      <div style={{ position: 'absolute', inset: '2px', background: '#a1a1aa', borderRadius: '1px' }} />
                    </div>
                  </div>
                </div>
                {/* Content area */}
                <div
                  className="device-preview-scroll"
                  style={{
                    background: '#ffffff',
                    overflow: 'hidden', overflowY: 'auto',
                    borderLeft: '2.5px solid #18181b', borderRight: '2.5px solid #18181b',
                    maxHeight: previewDevice === 'mobile' ? '812px' : '1024px',
                    scrollBehavior: 'smooth', scrollbarWidth: 'none', msOverflowStyle: 'none',
                    boxShadow: '0 0 40px rgba(0,0,0,0.12)',
                  }}
                >
                  <EditableRenderer {...rendererProps} />
                </div>
                {/* Home indicator */}
                <div style={{
                  background: '#18181b', borderRadius: '0 0 24px 24px',
                  padding: previewDevice === 'mobile' ? '8px 0 10px' : '12px 0 14px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 2px 20px rgba(0,0,0,0.15)',
                }}>
                  <div style={{
                    width: previewDevice === 'mobile' ? '100px' : '40px', height: '4px',
                    background: '#52525b', borderRadius: '2px',
                  }} />
                </div>
              </div>
            )}

            {/* ── Desktop canvas ── */}
            {previewDevice === 'desktop' && (
              <div style={{
                background: '#ffffff', minHeight: '600px',
                boxShadow: '0 0 0 1px rgba(0,0,0,0.04), 0 4px 24px rgba(0,0,0,0.08)',
              }}>
                {(!templateJSON?.blocks || templateJSON.blocks.length === 0) ? (
                  <div style={{
                    minHeight: '600px', display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center', padding: '60px 24px',
                    background: 'linear-gradient(180deg, #fafbfc 0%, #f1f3f5 100%)',
                  }}>
                    <div style={{
                      width: '80px', height: '80px', borderRadius: '20px',
                      background: 'linear-gradient(135deg, #eef2ff, #e0e7ff)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      marginBottom: '24px', boxShadow: '0 4px 16px rgba(99,102,241,0.1)',
                    }}>
                      <Sparkles style={{ width: '32px', height: '32px', color: '#6366f1' }} />
                    </div>
                    <h2 style={{
                      fontSize: '24px', fontWeight: 700, color: '#1e293b',
                      marginBottom: '8px', letterSpacing: '-0.02em',
                      fontFamily: "'Inter', -apple-system, sans-serif",
                    }}>
                      {pageType === 'about' ? 'Start Building Your Story'
                        : pageType === 'main' ? 'Design Your Home Page'
                        : `Design Your ${({shop:'Shop',product:'Product',cart:'Cart',checkout:'Checkout',contact:'Contact',navbar:'Navigation',footer:'Footer',privacy:'Privacy',shipping:'Shipping',terms:'Terms',faqs:'FAQ',auth:'Login'}[pageType] || pageType)} Page`}
                    </h2>
                    <p style={{
                      fontSize: '14px', color: '#64748b', maxWidth: '400px',
                      textAlign: 'center', lineHeight: 1.6, marginBottom: '32px',
                    }}>
                      {pageType === 'about'
                        ? 'Add sections from the library to create your about page. Choose from heroes, stories, team grids, timelines, and more.'
                        : 'This page is empty. Click the button below to add ready-made sections, or use the Sections panel in the toolbar above.'}
                    </p>
                    <button
                      onClick={() => setShowSectionLibrary(true)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '12px 28px', borderRadius: '12px',
                        background: '#6366f1', color: '#ffffff',
                        fontSize: '14px', fontWeight: 600, border: 'none',
                        cursor: 'pointer', transition: 'all 0.2s',
                        boxShadow: '0 2px 8px rgba(99,102,241,0.3)',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#4f46e5'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = '#6366f1'; e.currentTarget.style.transform = 'translateY(0)'; }}
                    >
                      <Sparkles style={{ width: '16px', height: '16px' }} />
                      Add Sections
                    </button>
                    {pageType === 'about' && (
                      <div style={{
                        marginTop: '48px', display: 'flex', gap: '24px',
                        color: '#94a3b8', fontSize: '12px',
                      }}>
                        {['Hero', 'Story', 'Team', 'Stats', 'Gallery', 'CTA'].map(cat => (
                          <span key={cat} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: '#c7d2fe', display: 'inline-block' }} />
                            {cat}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <EditableRenderer {...rendererProps} />
                )}
              </div>
            )}
          </div>

          {editMode && selectedElement && (
            <FloatingToolbar
              selectedElement={selectedElement}
              onAction={handleToolbarAction}
              containerRef={canvasRef}
              copiedStyle={copiedStyle}
            />
          )}
        </div>

        {/* ── Right panels ── */}
        {[
          { show: showPropertiesPanel && selectedElement, Panel: PropertiesPanel, props: {
            selectedElement,
            themeColors: templateJSON.theme?.colors,
            onChange: handleElementStyleChange,
            onClose: () => { setShowPropertiesPanel(false); setSelectedElement(null); setEditingNodePath(null); }
          }},
          { show: showThemePanel, Panel: ThemePanel, props: { theme: templateJSON.theme, onChange: handleThemeChange, onClose: () => setShowThemePanel(false) } },
          { show: showBusinessPanel, Panel: BusinessInfoPanel, props: { content: templateJSON.content, onChange: handleBusinessInfoChange, onClose: () => setShowBusinessPanel(false) } },
          { show: showSectionLibrary, Panel: SectionLibrary, props: { templateJSON, onAddSection: handleAddFromLibrary, onClose: () => setShowSectionLibrary(false), pageType } },
          { show: showVersionHistory, Panel: VersionHistory, props: { templateNo, accessToken, apiBaseUrl: API_BASE_URL, onRestore: handleRestoreVersion, onClose: () => setShowVersionHistory(false) } },
          { show: showAnimationPanel, Panel: AnimationPanel, props: { blocks: templateJSON?.blocks || [], onUpdateBlock: handleBlockAnimationUpdate, onClose: () => setShowAnimationPanel(false) } },
          { show: activeConfigPanel === 'navbar', Panel: NavbarConfigPanel, props: {
            attributes: pageType === 'navbar'
              ? findComponentAttributes(templateJSON, 'NavbarWrapper')
              : (navbarAttrs || {}),
            onAttributeChange: pageType === 'navbar'
              ? (newAttrs) => handleComponentAttributeChange('NavbarWrapper', newAttrs)
              : (newAttrs) => { setNavbarAttrs(prev => ({ ...prev, ...newAttrs })); navbarDirtyRef.current = true; setIsDirty(true); },
            onClose: () => setActiveConfigPanel(null),
          }},
          { show: activeConfigPanel === 'footer', Panel: FooterConfigPanel, props: {
            attributes: pageType === 'footer'
              ? findComponentAttributes(templateJSON, 'FooterWrapper')
              : (footerAttrs || {}),
            onChange: pageType === 'footer'
              ? (newAttrs) => handleComponentAttributeChange('FooterWrapper', newAttrs)
              : (newAttrs) => { setFooterAttrs(prev => ({ ...prev, ...newAttrs })); footerDirtyRef.current = true; setIsDirty(true); },
            onClose: () => setActiveConfigPanel(null),
          }},
        ].map(({ show, Panel, props }, i) => (
          <div
            key={i}
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              width: show ? '320px' : '0px', opacity: show ? 1 : 0,
              transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease',
              overflow: 'hidden', flexShrink: 0,
            }}
          >
            {show && <Panel {...props} />}
          </div>
        ))}
      </div>

      {/* ── Undo/Redo toast — minimal, non-intrusive ── */}
      {undoToast && (
        <div style={{
          position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)',
          zIndex: 100, background: 'rgba(15, 23, 42, 0.92)', color: '#fff',
          padding: '10px 18px',
          borderRadius: '10px', boxShadow: '0 4px 20px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.05)',
          fontSize: '12px', display: 'flex', alignItems: 'center', gap: '8px',
          animation: 'toastIn 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
          backdropFilter: 'blur(16px)', fontFamily: "'Inter', -apple-system, sans-serif",
          letterSpacing: '0.01em',
        }}>
          <span style={{
            fontWeight: 600, color: undoToast.action === 'Undo' ? '#93c5fd' : '#86efac',
            fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em',
          }}>{undoToast.action}</span>
          <span style={{ width: '1px', height: '12px', background: 'rgba(255,255,255,0.1)' }} />
          <span style={{ opacity: 0.6, fontWeight: 400 }}>{undoToast.description}</span>
        </div>
      )}

      {/* ── Global styles ── */}
      <style>{`
        .device-preview-scroll::-webkit-scrollbar { display: none; }
        .device-preview-scroll { scrollbar-width: none; -ms-overflow-style: none; }
        /* Smooth zoom transitions */
        .zoom-container { transition: zoom 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
        @keyframes hintSlideIn { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes toastIn { from { opacity: 0; transform: translate(-50%, 12px); } to { opacity: 1; transform: translate(-50%, 0); } }

        /* Smooth scrollbar for the canvas area */
        div::-webkit-scrollbar { width: 8px; height: 8px; }
        div::-webkit-scrollbar-track { background: transparent; }
        div::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.12); border-radius: 4px; border: 2px solid transparent; background-clip: padding-box; }
        div::-webkit-scrollbar-thumb:hover { background: rgba(0,0,0,0.2); border: 2px solid transparent; background-clip: padding-box; }

        /* Selection highlight */
        [data-selected="true"] { outline: 2px solid #6366f1 !important; outline-offset: 1px; }

        /* Focus ring for accessibility */
        button:focus-visible { outline: 2px solid #6366f1; outline-offset: 2px; }

        /* Smooth transitions for panel open/close */
        * { -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
      `}</style>
    </div>
  );
};

export default VisualTemplateEditor;
